import { GetterTree, MutationTree, ActionTree } from 'vuex'
import Vue from 'vue'
import * as T from '@/types'

import api from '@/api'
import crud from '@/store/store-crud'

import { mixin } from '@/store/utils'

const INITIAL_STATE: () => Partial<T.Store.FilesState> = () => ({
    loading: false,
    loaded: false,
    saving: false,
    saved: false,
    deleting: false,
    sorts: {createdAt: false},
    filters: {},
    downloading: [],
    failed: [],
    successful: [],
    brokenLinks: [],
})

const getters: GetterTree<T.Store.FilesState, T.Store.RootState> = {

    filters: (state) => state.filters,

    sorts: (state) => state.sorts,

    sortKeys: (state) => Object.keys(state.sorts),

    isAscending: (state) => (key: keyof T.File) => {
        if (!state.sorts || !state.sorts[key]) return false
        return state.sorts[key]
    },

    downloading: (state) => state.downloading,

    failed: (state) => state.failed,

    successful: (state) => state.successful,

    brokenLinks: (state) => state.brokenLinks,

}

const mutations: MutationTree<T.Store.FilesState> = {

    setFilter (state, payload: { key: keyof T.File, value: any }) {
        Vue.set(state.filters, payload.key, payload.value)
    },

    setFilters (state, filters) {
        state.filters = filters
    },

    removeFilter (state, payload: { key: keyof T.File }) {
        Vue.delete(state.filters, payload.key)
    },

    clearFilter (state, payload: { key: keyof T.File }) {
        Vue.set(state.filters, payload.key, null)
    },

    clearFilters (state) {
        state.filters = {}
    },

    applySort (state, payload: { key: keyof T.File, ascending: boolean }) {
        Vue.set(state.sorts, payload.key, payload.ascending)
    },

    setSorts (state, sorts) {
        state.sorts = sorts
    },

    beginDownloading (state, payload: { fileIds: T.FileId[] }) {
        state.downloading = state.downloading.filter((fileId) => {
            return !payload.fileIds.includes(fileId)
        }).concat(payload.fileIds)
    },

    finishDownloading (state, payload: { fileIds: T.FileId[] }) {
        state.downloading = state.downloading.filter((fileId) => {
            return !payload.fileIds.includes(fileId)
        })
    },

    setFailed (state, payload: { fileIds: T.FileId, failed: boolean }) {
        state.failed = state.failed.filter((fileId) => {
            return !payload.fileIds.includes(fileId)
        })
        if (payload.failed) state.failed = state.failed.concat(payload.fileIds)
    },

    setSuccessful (state, payload: { fileIds: T.FileId }) {
        state.successful = state.successful.filter((fileId) => {
            return !payload.fileIds.includes(fileId)
        }).concat(payload.fileIds)
    },

    setBroken (state, payload: { fileIds: T.FileId }) {
        state.brokenLinks = state.brokenLinks.filter((fileId) => {
            return !payload.fileIds.includes(fileId)
        }).concat(payload.fileIds)
    },
}

const actions: ActionTree<T.Store.FilesState, T.Store.RootState> = {

    async refresh ({ commit, getters }) {
        commit('setLoading', true)
        try {
            const items = await api.files.all(getters.filters, {sort: getters.sorts})
            commit('setItems', items)
        } catch (error) {
            console.log('Refresh error:', error)
        } finally {
            commit('setLoading', false)
        }
    },

    setFilter ({ commit }, payload: { key: keyof T.File, value: any }) {
        const { key, value } = payload
        commit('setFilter', { key, value })
    },

    clearFilter ({ commit }, payload: { key: keyof T.File }) {
        const { key } = payload
        commit('clearFilter', { key })
    },

    removeFilter ({ commit }, payload: { key: keyof T.File }) {
        const { key } = payload
        commit('removeFilter', { key })
    },

    clearFilters ({ commit }) {
        commit('clearFilters')
    },

    applySort ({ commit }, payload: { key: keyof T.File, value: any } ) {
        const { key, value } = payload
        commit('applySort', {key, ascending: value})
    },

    async downloadFiles ({ commit }, payload: { fileIds: T.FileId[], redirect? : boolean}) {
        const { fileIds, redirect } = payload
        commit('setFailed', { fileIds, failed: false})
        commit('beginDownloading', { fileIds })
        try {
            await api.files.download(fileIds, redirect)
            commit('setSuccessful', { fileIds })
        } catch(error){
            console.log('Download error:', {error})
            if (error.response && error.response.status === 422) {
                // File is not present on the server - something is wrong in the backend.
                const { errors } = error.response.data
                const brokenFileIds = errors.map((file: any) => {
                    return file.source.pointer
                })
                const fileIdsToRetry = fileIds.filter((fileId: any) => {
                    return !brokenFileIds.includes(fileId)
                })
                console.log('brokenFileIds',brokenFileIds)
                console.log('fileIdsToRetry',fileIdsToRetry)
                commit('setBroken', { fileIds: brokenFileIds })
                commit('setFailed', { fileIds: fileIdsToRetry, failed: true })
            }
            else {
                console.log('Failed')
                commit('setFailed', { fileIds, failed: true })
            } 
        }
        commit('finishDownloading', { fileIds })
    },
}

const module = (mixin<T.Store.FilesState, T.Store.RootState>())(
    [crud<T.File>(api.files)],
    // [crud<T.File>(api.files), selectable<T.File>()],
    {
        namespaced: true,
        state: INITIAL_STATE(),
        actions,
        getters,
        mutations,
    },
)

export default module
